import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Edit, Plus, Trash2, Users, Building2, Hotel, Trees, Building, Layers, DoorOpen, House, PanelsTopLeft, Handshake, Megaphone, type LucideIcon } from 'lucide-react';
import { AudienceNode, AudienceNodeType, isPartner } from '@/services/audienceService';
import { useTranslation } from '@/hooks/useTranslation';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';

const nodeIcons: Record<AudienceNodeType, LucideIcon> = {
  COMPLEX: Building2,
  HOTEL: Hotel,
  RESIDENTIAL: Trees,
  BUILDING: Building,
  FLOOR: Layers,
  ROOM: DoorOpen,
  HOUSE: House,
  APARTMENT: PanelsTopLeft,
  PARTNER: Handshake,
  PROGRAM: Megaphone,
};

interface Props {
  nodes: AudienceNode[]; selected?: string;
  onSelect: (node: AudienceNode) => void; onEdit: (node: AudienceNode) => void;
  onDelete: (node: AudienceNode) => void; onCreate: () => void;
  onCreateChild: (child: Omit<AudienceNode, 'id'>) => Promise<void>;
}
export default function AudienceTree({ nodes, selected, onSelect, onEdit, onDelete, onCreate, onCreateChild }: Props) {
  const { t } = useTranslation();
  const c = t.audience;
  const layoutOptions = [
    { value: 'DEFAULT', label: t.applicationSettings.layouts.default },
    { value: 'VERTICAL_MENU', label: t.applicationSettings.layouts.verticalMenu },
    { value: 'HOSPITALITY', label: t.applicationSettings.layouts.hospitality },
  ];
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Omit<AudienceNode, 'id'> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  function startChild(parent: AudienceNode) {
    setExpanded(prev => new Set([...prev, parent.id]));
    setError(null);
    setDraft({ name: '', parentId: parent.id, type: parent.type === 'COMPLEX' ? 'HOTEL' : parent.type === 'RESIDENTIAL' ? 'HOUSE' : parent.type === 'HOTEL' ? 'ROOM' : 'APARTMENT' });
  }
  async function saveChild(event: React.FormEvent) {
    event.preventDefault();
    if (!draft || saving) return;
    setSaving(true); setError(null);
    try { await onCreateChild({ ...draft, name: draft.name.trim() }); setDraft(null); }
    catch (e) { setError(e instanceof Error ? e.message : c.loadError); }
    finally { setSaving(false); }
  }
  const matches = new Set<string>();
  const byId = new Map(nodes.map(n => [n.id, n]));
  const children = new Map<string, AudienceNode[]>();
  for (const n of nodes) {
    const key = n.parentId && byId.has(n.parentId) ? n.parentId : '';
    children.set(key, [...(children.get(key) ?? []), n]);
    if (`${n.name} ${c.types[n.type]}`.toLocaleLowerCase().includes(search.toLocaleLowerCase())) {
      let current: AudienceNode | undefined = n;
      const seen = new Set<string>();
      while (current && !seen.has(current.id)) {
        seen.add(current.id); matches.add(current.id); current = byId.get(current.parentId ?? '');
      }
    }
  }
  const rows: { node: AudienceNode; depth: number }[] = [];
  const visited = new Set<string>();
  function visit(parent: string, depth: number) {
    for (const node of children.get(parent) ?? []) {
      if (visited.has(node.id) || (search && !matches.has(node.id))) continue;
      visited.add(node.id); rows.push({ node, depth });
      if (search || expanded.has(node.id)) visit(node.id, depth + 1);
    }
  }
  visit('', 0);
  return <div className="overflow-hidden bg-white shadow">
    <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
      <h3 className="font-medium text-gray-900">{c.groups}</h3>
      <span className="rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">{nodes.length}</span>
      <div className="min-w-40 flex-1"><Input aria-label={c.search} placeholder={c.search} disabled={Boolean(draft)} value={search} onChange={e => setSearch(e.target.value)} /></div>
      <Button size="sm" disabled={Boolean(draft)} onClick={() => onCreate()}><Plus className="mr-1 h-4 w-4" />{c.newNode}</Button>
      <button type="button" title={c.expandAll} aria-label={c.expandAll} className="cursor-pointer rounded p-2 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed" onClick={() => setExpanded(new Set(nodes.map(n => n.id)))}><ChevronsUpDown size={18} aria-hidden="true" /></button>
      <button type="button" title={c.collapseAll} aria-label={c.collapseAll} className="cursor-pointer rounded p-2 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed" disabled={Boolean(draft)} onClick={() => setExpanded(new Set())}><ChevronsDownUp size={18} aria-hidden="true" /></button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500"><tr><th className="px-4 py-1">{c.name}</th><th className="px-3 py-1">{c.type}</th><th className="px-3 py-1">{c.layout}</th><th className="relative px-3 py-1"><span className="sr-only">{c.actions}</span></th></tr></thead>
        <tbody className="divide-y divide-gray-100">{rows.map(({ node, depth }) => {
          const TypeIcon = nodeIcons[node.type];
          const hasChildren = Boolean(children.get(node.id)?.length);
          const open = Boolean(search) || expanded.has(node.id);
          const canAddChild = !isPartner(node) && !['ROOM', 'HOUSE', 'APARTMENT'].includes(node.type);
          return <Fragment key={node.id}><tr className={selected === node.id ? 'bg-indigo-50' : 'hover:bg-gray-50'}>
            <td className="py-1 pr-3" style={{ paddingLeft: 12 + depth * 24 }}>
              <div className="flex items-center gap-1">
                {hasChildren ? <button aria-label={`${open ? c.collapseAll : c.expandAll}: ${node.name}`} aria-expanded={open} disabled={Boolean(draft)} className="cursor-pointer disabled:cursor-not-allowed rounded p-1 hover:bg-gray-200" onClick={() => setExpanded(previous => { const next = new Set(previous); if (next.has(node.id)) next.delete(node.id); else next.add(node.id); return next; })}>{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button> : <span className="w-6 shrink-0" />}
                <button className="inline-flex items-center gap-2 cursor-pointer disabled:cursor-not-allowed text-left font-medium text-gray-900 hover:text-indigo-600" aria-pressed={selected === node.id} onClick={() => onSelect(node)}><TypeIcon size={16} aria-hidden="true" className="shrink-0 text-gray-500" /><span>{node.name}</span></button>
                {hasChildren && <span className="ml-1 text-xs text-gray-400">({children.get(node.id)?.length})</span>}
              </div>
            </td>
            <td className="px-3 py-1 text-gray-600">{c.types[node.type]}</td>
            <td className="px-3 py-1 text-gray-600">{layoutOptions.find(option => option.value === node.layout)?.label ?? c.inherit}</td>
            <td className="px-3 py-1"><div className="flex justify-end gap-1">
              <button title={c.members} aria-label={`${c.members}: ${node.name}`} className="cursor-pointer disabled:cursor-not-allowed rounded p-2 text-indigo-600 hover:bg-indigo-100" onClick={() => onSelect(node)}><Users size={16} /></button>
              {canAddChild && <button title={c.addChild} aria-label={`${c.addChild}: ${node.name}`} className="cursor-pointer disabled:cursor-not-allowed rounded p-2 text-gray-600 hover:bg-gray-100" disabled={Boolean(draft)} onClick={() => startChild(node)}><Plus size={16} /></button>}
              <button disabled={Boolean(draft)} title={c.edit} aria-label={`${c.edit}: ${node.name}`} className="cursor-pointer disabled:cursor-not-allowed rounded p-2 text-gray-600 hover:bg-gray-100" onClick={() => onEdit(node)}><Edit size={16} /></button>
              <button disabled={Boolean(draft)} title={c.delete} aria-label={`${c.delete}: ${node.name}`} className="cursor-pointer disabled:cursor-not-allowed rounded p-2 text-red-600 hover:bg-red-50" onClick={() => onDelete(node)}><Trash2 size={16} /></button>
            </div></td>
          </tr>
          {draft?.parentId === node.id && <tr className="bg-indigo-50/50"><td colSpan={4} className="py-1 pr-4" style={{ paddingLeft: 36 + depth * 24 }}>
            <form onSubmit={saveChild} aria-label={`${c.addChild}: ${node.name}`} onKeyDown={event => { if (event.key === 'Escape' && !saving) { event.preventDefault(); setDraft(null); } }}>
              <fieldset disabled={saving} className="grid gap-3 sm:grid-cols-3">
                <Input autoFocus label={c.name} aria-label={c.name} required maxLength={150} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
                <Select className="cursor-pointer disabled:cursor-not-allowed" label={c.type} aria-label={c.type} value={draft.type}
                  options={Object.entries(c.types).filter(([type]) => !['PARTNER', 'PROGRAM'].includes(type) && (node.type === 'COMPLEX' || !['COMPLEX', 'HOTEL', 'RESIDENTIAL'].includes(type))).map(([value, label]) => ({ value, label }))}
                  onChange={e => setDraft({ ...draft, type: e.target.value as AudienceNodeType })} />
                <Select className="cursor-pointer disabled:cursor-not-allowed" label={c.layout} aria-label={c.layout} allowEmpty emptyOptionLabel={c.inherit} value={draft.layout ?? ''}
                  options={layoutOptions} onChange={e => setDraft({ ...draft, layout: (e.target.value || null) as AudienceNode['layout'] })} />
              </fieldset>
              {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
              <div className="mt-3 flex justify-end gap-2"><Button type="button" size="sm" variant="outline" disabled={saving} onClick={() => setDraft(null)}>{c.cancel}</Button><Button type="submit" size="sm" loading={saving} disabled={!draft.name.trim()}>{c.save}</Button></div>
            </form>
          </td></tr>}
          </Fragment>;
        })}</tbody>
      </table>
      {!rows.length && <p className="p-8 text-center text-sm text-gray-500">{c.noGroups}</p>}
    </div>
  </div>;
}
